/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IFilterField } from "Interfaces/IFilterField";
import { IKeyText } from "Interfaces/IKeyText";

import { fieldTypes } from "./FilterFields";
import { TenantTypesEnum } from "./TenantTypes";

export const defaultListingPageSize = 16;

export enum BenefitStatusEnum {
  Unclaimed = "Unclaimed",
  Claimed = "Claimed",
  Expired = "Expired",
}

export enum BenefitNameEnum {
  ManageCodes = "Manage Codes",
  AzureRequests = "AzureRequests",
  Azure = "Azure Credits",
  GitHubPro = "GitHub Pro",
  LinkedInLearning = "LinkedIn",
  VisualStudio = "Visual Studio",
  O365 = "O365",
  M365 = "M365",
  M365Copilot = "M365 Copilot",
  MCP = "MCP Exam Voucher",
  Teams = "Teams",
  AwardKit = "AwardKit",
  SwagKit = "SwagKit",
  AwardKits = "MVP Award Kits",
  MS365 = "MS365",
  BenefitManager = "benefitManagerItems", // used to render yaml based benefits
}

export enum BenefitDisplayNameEnum {
  ManageCodes = "Manage Codes",
  AzureRequests = "Azure Requests",
  Azure = "Extended Azure Credit",
  GitHubPro = "GitHub Pro",
  LinkedInLearning = "LinkedIn Learning",
  VisualStudio = "Visual Studio",
  O365 = "Office 365",
  M365Copilot = "M365 Copilot",
  MCP = "MCP Exam Voucher",
  Teams = "Teams",
  AwardKit = "MVP Award Kit",
  SwagKit = "Student Ambassador Swag kit",
  AwardKits = "MVP Award Kits",
  MS365 = "Microsoft 365",
}

export const benefitNameToResultMap = {
  [BenefitNameEnum.ManageCodes]: "manageCodes",
  [BenefitNameEnum.AzureRequests]: "azureRequests",
  [BenefitNameEnum.Azure]: "azure",
  [BenefitNameEnum.GitHubPro]: "githubPro",
  [BenefitNameEnum.MCP]: "mcp",
  [BenefitNameEnum.LinkedInLearning]: "linkedIn",
  [BenefitNameEnum.Teams]: "teams",
  [BenefitNameEnum.VisualStudio]: "visualStudio",
  [BenefitNameEnum.O365]: "O365",
  [BenefitNameEnum.M365]: "m365",
  [BenefitNameEnum.M365Copilot]: "m365Copilot",
  [BenefitNameEnum.AwardKits]: "awardKit",
  [BenefitNameEnum.SwagKit]: "swagKit",
  [BenefitNameEnum.MS365]: "ms365",
};

export const BenefitsSubSiteUrlPath = Object.freeze({
  manageCodes: "manageCodes",
  azureRequests: "azureRequests",
  gitHubPro: "gitHubPro",
  linkedInLearning: "linkedInLearning",
  O365: "O365",
  M365: "M365",
  m365Copilot: "m365Copilot",
  mcp: "mcp",
  teams: "teams",
  awardKit: "awardKit",
  visualStudio: "visualStudio",
  swagKit: "swagKit",
  MS365: "MS365",
});

export const benefitStatus: IKeyText[] = [
  {
    key: "Unclaimed",
    text: "Unclaimed",
  },
  {
    key: "Claimed",
    text: "Claimed",
  },
  {
    key: "Expired",
    text: "Expired",
  },
];

export const AWARD_PERIOD = "awardPeriod";
export const BENEFIT_STATUS = "benefitStatus";
export const CLAIMED_FROM = "claimedFrom";
export const CLAIMED_TO = "claimedTo";
export const UPLOADED_FROM = "uploadedFrom";
export const UPLOADED_TO = "uploadedTo";
export const EXPIRATION_START_DATE = "expirationStartDate";
export const EXPIRATION_END_DATE = "expirationEndDate";
export const COUNTRY_CODE_POOL = "countryCodePool";
export const SKU = "sku";
export const SWAG_KIT = "levelStatusName";
export const ALLOWED_BENEFITS_FILE_UPLOAD_SIZE = 1; // MB
/**
 * Sidebar filter constants used for rendering
 */
export const benefitFilterFields: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.MULTI_SELECT,
    options: benefitStatus,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * default filter values for benefits.
 */
export const defaultFilterValues: {
  fieldName: string;
  value: string;
}[] = [];

export const tenantBenefitTypes = {
  [TenantTypesEnum.MSP]: [BenefitNameEnum.VisualStudio, BenefitNameEnum.SwagKit],
  [TenantTypesEnum.MVP]: [
    BenefitNameEnum.Azure,
    BenefitNameEnum.GitHubPro,
    BenefitNameEnum.LinkedInLearning,
    BenefitNameEnum.MS365,
    BenefitNameEnum.M365, // This will be now O365
    BenefitNameEnum.M365Copilot,
    BenefitNameEnum.MCP,
    BenefitNameEnum.Teams,
    BenefitNameEnum.VisualStudio,
    BenefitNameEnum.AwardKits,
    BenefitNameEnum.BenefitManager, // Yaml based benefits(only for MVPs). Keep it at last, so that it will be rendered at last in the list.
  ],
  [TenantTypesEnum.RD]: [
    BenefitNameEnum.Azure,
    BenefitNameEnum.GitHubPro,
    BenefitNameEnum.LinkedInLearning,
    BenefitNameEnum.MS365,
    BenefitNameEnum.M365, // This will be now O365
    BenefitNameEnum.Teams,
    BenefitNameEnum.VisualStudio,
  ],
};

/**
 * Configure additional filter name with benefits(url path)
 */
export const additionalFilter = {
  countryCodePool: ["teams", "O365", "MS365"],
  swagKit: ["swagKit"],
  sku: ["awardKit"],
};

/**
 * Field constant used for rendering in data-table for Teams and O365 benefits
 */
export const CountryCodePoolColumn = {
  key: "countryCodePool",
  name: "Country Code Pool",
  fieldName: "countryCodePoolName",
  minWidth: 160,
  isResizable: true,
};

/**
 * Drop-down options for Teams - country code pool
 */
export const teamsCountryCodePool: IKeyText[] = [
  {
    key: "Teams",
    text: "Any",
  },
  {
    key: "Teams-ROW",
    text: "ROW",
  },
  {
    key: "Teams-EEA",
    text: "EEA",
  },
];

/**
 * Drop-down options for O365 - country code pool
 */
export const o365CountryCodePool = [
  {
    key: "O365",
    text: "Any",
  },
  {
    key: "O365-E3-ROW",
    text: "ROW",
    values: ["O365-E3-ROW"], // used to display in data-table field
  },
  {
    key: "O365-E5-ROW-Established",
    text: "ROW-Established Market",
    values: ["O365-E5-ROW-Established"], // used to display in data-table field
  },
  {
    key: "O365-E5-ROW-Emerging",
    text: "ROW-Emerging Market",
    values: ["O365-E5-ROW-Emerging"], // used to display in data-table field
  },
  {
    key: "O365-EEA",
    text: "EEA",
    values: ["O365-E3-EEA", "O365-E5-EEA"], // used to display in data-table field
  },
];

/**
 * Drop-down options for MS365 - country code pool
 */
export const ms365CountryCodePool = [
  {
    key: "MS365",
    text: "Any",
  },
  {
    key: "MS365-E5-ROW-Established",
    text: "ROW-Established Market",
  },
  {
    key: "MS365-E5-ROW-Emerging",
    text: "ROW-Emerging Market",
  },
  {
    key: "MS365-E5-EEA",
    text: "EEA",
  },
];

/**
 * Sidebar filter constants used for rendering in Teams benefits
 * Additional filter : Country Code Pool
 */
export const benefitTeamsFilterWithCountryCodePool: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.SELECT,
    options: benefitStatus,
  },
  {
    key: COUNTRY_CODE_POOL,
    text: "Country Code Pool",
    type: fieldTypes.SELECT,
    options: teamsCountryCodePool,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Sidebar filter constants used for rendering in O365 benefits
 * Additional filter : Country Code Pool
 */
export const benefitO365FilterWithCountryCodePool: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.MULTI_SELECT,
    options: benefitStatus,
  },
  {
    key: COUNTRY_CODE_POOL,
    text: "Country Code Pool",
    type: fieldTypes.SELECT,
    options: o365CountryCodePool,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Sidebar filter constants used for rendering in MS365 benefits
 * Additional filter : Country Code Pool
 */
export const benefitMS365FilterWithCountryCodePool: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.MULTI_SELECT,
    options: benefitStatus,
  },
  {
    key: COUNTRY_CODE_POOL,
    text: "Country Code Pool",
    type: fieldTypes.SELECT,
    options: ms365CountryCodePool,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Field constant used for rendering in data-table for SA Swag Kit
 */
export const SwagKitColumn = {
  key: "levelStatusName",
  name: "Swag Kit",
  fieldName: "levelStatusName",
  minWidth: 100,
  isResizable: true,
};

/**
 * Drop-down options for SA - Swag Kit type
 */
export const swagKitOptions = [
  {
    key: "",
    text: "Any",
  },
  {
    key: "Beta",
    text: "Beta",
  },
  {
    key: "Gold",
    text: "Gold",
  },
];

/**
 * Sidebar filter constants used for rendering in Swag Kit
 * Additional filter : Swag Kit
 */
export const benefitSwagKitFilter: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.MULTI_SELECT,
    options: benefitStatus,
  },
  {
    key: SWAG_KIT,
    text: "Swag Kit",
    type: fieldTypes.SELECT,
    options: swagKitOptions,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];

/**
 * Field constant used for rendering in data-table for MVP Award Kit
 */
export const SKUColumn = {
  key: "sku",
  name: "SKU",
  fieldName: "sku",
  minWidth: 160,
  isResizable: true,
};

/**
 * Sidebar filter constants used for rendering in Award Kit benefits
 * Additional filter : SKU
 */
export const benefitAwardKitFilter: IFilterField[] = [
  {
    key: BENEFIT_STATUS,
    text: "Benefit Status",
    type: fieldTypes.MULTI_SELECT,
    options: benefitStatus,
  },
  {
    key: SKU,
    text: "SKU",
    type: fieldTypes.SKU,
  },
  {
    key: CLAIMED_FROM,
    text: "Claimed From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: CLAIMED_TO,
    text: "Claimed To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_FROM,
    text: "Uploaded From",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: UPLOADED_TO,
    text: "Uploaded To",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_START_DATE,
    text: "Expiration Start Date",
    type: fieldTypes.DATE_PICKER,
  },
  {
    key: EXPIRATION_END_DATE,
    text: "Expiration End Date",
    type: fieldTypes.DATE_PICKER,
  },
];
